import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Divider,
  Tooltip,
  ButtonGroup,
  InputAdornment
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Edit,
  Warning,
  Info,
  Save,
  Close,
  Undo,
  SkipNext
} from '@mui/icons-material';
import { performanceApi, penaltyApi } from './api';
import { useSchoolFeesMode } from './SchoolFeesContext';

const PenaltyMemberRow = ({ 
  member, 
  savedPenalty, 
  onActionComplete, 
  formatCurrency,
  groupId,
  currentPeriod,
  isSchoolFees
}) => {
  const [action, setAction] = useState(savedPenalty ? savedPenalty.status : 'pending');
  const [customAmount, setCustomAmount] = useState(member.penalty_amount || 0);
  const [customReason, setCustomReason] = useState(member.reason || '');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedPenaltyData, setSavedPenaltyData] = useState(savedPenalty);

  // Check if this member already has a saved penalty
  const hasSavedPenalty = !!savedPenaltyData;

  const handleApply = async () => {
    setSaving(true);
    try {
      const actionData = {
        member_id: member.member_id,
        action: 'apply',
        penalty_data: {
          penalty_type: member.penalty_type,
          penalty_amount: member.penalty_amount,
          penalty_rate: member.penalty_rate,
          base_amount: member.base_amount,
          balance_before: member.balance_before,
          reason: member.reason,
          period_start: currentPeriod.period_start,
          period_end: currentPeriod.period_end,
          period_name: currentPeriod.period_name,
          is_system_generated: true,
          is_custom: false,
          is_school_fees: member.penalty_type === 'advance_default' ? isSchoolFees : false
        }
      };
      
      const response = await penaltyApi.savePenaltyAction(groupId, actionData);

      if (response.success) {
        setAction('pending');
        setSavedPenaltyData(response);
        onActionComplete(member.member_id, {
          action: 'apply',
          penalty_id: response.penalty_id,
          saved: true,
          ...response
        });
      }
    } catch (error) {
      console.error('Error applying penalty:', error);
      alert('Failed to save penalty: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleWaive = async () => {
    setSaving(true);
    try {
      const response = await penaltyApi.savePenaltyAction(groupId, {
        member_id: member.member_id,
        action: 'waive',
        penalty_data: {
          penalty_type: member.penalty_type,
          penalty_amount: 0,
          penalty_rate: member.penalty_rate,
          base_amount: member.base_amount,
          balance_before: member.balance_before,
          reason: 'Penalty waived',
          period_start: currentPeriod.period_start,
          period_end: currentPeriod.period_end,
          period_name: currentPeriod.period_name,
          is_system_generated: false,
          is_custom: false,
          is_school_fees: member.penalty_type === 'advance_default' ? isSchoolFees : false
        }
      });

      if (response.success) {
        setAction('waived');
        setSavedPenaltyData(response);
        onActionComplete(member.member_id, {
          action: 'waive',
          penalty_id: response.penalty_id,
          saved: true,
          ...response
        });
      }
    } catch (error) {
      console.error('Error waiving penalty:', error);
      alert('Failed to waive penalty: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCustomize = () => {
    setEditing(true);
  };

  const handleSaveCustom = async () => {
    setSaving(true);
    try {
      const response = await penaltyApi.savePenaltyAction(groupId, {
        member_id: member.member_id,
        action: 'customize',
        penalty_data: {
          penalty_type: member.penalty_type,
          penalty_amount: parseFloat(customAmount),
          penalty_rate: member.penalty_rate,
          base_amount: member.base_amount,
          balance_before: member.balance_before,
          reason: customReason || member.reason,
          period_start: currentPeriod.period_start,
          period_end: currentPeriod.period_end,
          period_name: currentPeriod.period_name,
          is_system_generated: false,
          is_custom: true,
          is_school_fees: member.penalty_type === 'advance_default' ? isSchoolFees : false
        }
      });

      if (response.success) {
        setAction('pending');
        setEditing(false);
        setSavedPenaltyData(response);
        onActionComplete(member.member_id, {
          action: 'customize',
          penalty_id: response.penalty_id,
          saved: true,
          is_custom: true,
          ...response
        });
      }
    } catch (error) {
      console.error('Error customizing penalty:', error);
      alert('Failed to save custom penalty: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async () => {
    if (!savedPenaltyData?.penalty_id) return;

    setSaving(true);
    try {
      const response = await penaltyApi.revertPenaltyAction(
        groupId, 
        savedPenaltyData.penalty_id
      );

      if (response.success) {
        setAction('pending');
        setSavedPenaltyData(null);
        onActionComplete(member.member_id, {
          action: 'revert',
          reverted: true
        });
      }
    } catch (error) {
      console.error('Error reverting penalty:', error);
      alert('Failed to revert penalty: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setCustomAmount(member.penalty_amount || 0);
    setCustomReason(member.reason || '');
  };

  const getStatusChip = () => {
    if (saving) return <Chip size="small" label="Saving..." color="default" />;
    if (editing) return <Chip size="small" label="Editing" color="info" />;
    
    if (hasSavedPenalty) {
      if (savedPenaltyData.status === 'waived') {
        return <Chip size="small" label="Waived (Saved)" color="error" icon={<Cancel />} />;
      }
      if (savedPenaltyData.is_custom) {
        return <Chip size="small" label="Customized (Saved)" color="primary" icon={<Edit />} />;
      }
      return <Chip size="small" label="Applied (Saved)" color="success" icon={<CheckCircle />} />;
    }
    
    return <Chip size="small" label="Pending Action" color="warning" />;
  };

  if (editing) {
    return (
      <TableRow>
        <TableCell>{member.member_name}</TableCell>
        <TableCell>
          <Chip 
            size="small" 
            label={member.penalty_type_display} 
            color="warning"
          />
        </TableCell>
        <TableCell align="right">
          <TextField
            size="small"
            type="number"
            value={customAmount}
            onChange={(e) => setCustomAmount(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">KES</InputAdornment>
            }}
          />
        </TableCell>
        <TableCell>
          <TextField
            size="small"
            multiline
            rows={2}
            value={customReason}
            onChange={(e) => setCustomReason(e.target.value)}
            placeholder="Custom reason..."
          />
        </TableCell>
        <TableCell>
          <Chip size="small" label="Editing" color="info" />
        </TableCell>
        <TableCell>
          <ButtonGroup size="small">
            <Tooltip title="Save Changes">
              <IconButton 
                size="small" 
                color="primary" 
                onClick={handleSaveCustom}
                disabled={saving}
              >
                {saving ? <CircularProgress size={16} /> : <Save />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel">
              <IconButton 
                size="small" 
                color="secondary" 
                onClick={handleCancelEdit}
                disabled={saving}
              >
                <Close />
              </IconButton>
            </Tooltip>
          </ButtonGroup>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow sx={{ 
      backgroundColor: hasSavedPenalty ? 
        (savedPenaltyData.status === 'waived' ? '#ffebee' : 
         savedPenaltyData.is_custom ? '#e3f2fd' : '#f1f8e9') : 
        'inherit'
    }}>
      <TableCell>
        <Typography variant="body2" fontWeight="medium">
          {member.member_name}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          ID: {member.member_reference_id}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip 
          size="small" 
          label={member.penalty_type_display} 
          color={member.penalty_type === 'loan_default' ? 'error' : 'warning'}
        />
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" fontWeight="medium">
          {formatCurrency(
            savedPenaltyData?.penalty_amount || member.penalty_amount
          )}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Rate: {(member.penalty_rate * 100).toFixed(1)}%
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {savedPenaltyData?.reason || member.reason}
        </Typography>
        {member.analysis_data && (
          <Typography variant="caption" color="textSecondary">
            Base: {formatCurrency(member.base_amount)}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {getStatusChip()}
      </TableCell>
      <TableCell>
        {hasSavedPenalty ? (
          <Box>
            <Tooltip title="Revert this action">
              <Button
                size="small"
                color="warning"
                onClick={handleRevert}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} /> : <Undo />}
                fullWidth
              >
                Revert
              </Button>
            </Tooltip>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {savedPenaltyData.status === 'waived' 
                ? 'Will not be applied' 
                : 'Will apply on close'}
            </Typography>
          </Box>
        ) : (
          <ButtonGroup size="small" orientation="vertical">
            <Button
              size="small"
              color="success"
              onClick={handleApply}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : <CheckCircle />}
            >
              Apply
            </Button>
            <Button
              size="small"
              color="warning"
              onClick={handleWaive}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : <Cancel />}
            >
              Waive
            </Button>
            <Button
              size="small"
              color="primary"
              onClick={handleCustomize}
              disabled={saving}
              startIcon={<Edit />}
            >
              Customize
            </Button>
          </ButtonGroup>
        )}
      </TableCell>
    </TableRow>
  );
};

const ClosePerformance = ({ 
  open, 
  onClose, 
  currentPeriod, 
  groupId, 
  onSuccess, 
  onError,
  onUpdatePerformanceAnalysis,
  isSchoolFeesMode = false
}) => {
  const [closingNotes, setClosingNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const { getModeForGroup } = useSchoolFeesMode();
  const isSchoolFees = isSchoolFeesMode || getModeForGroup(groupId);
  
  // Penalty-related state
  const [penaltyPreview, setPenaltyPreview] = useState(null);
  const [savedPenalties, setSavedPenalties] = useState({});
  const [loadingPenalties, setLoadingPenalties] = useState(false);
  const [memberActions, setMemberActions] = useState({});
  const [allMembersProcessed, setAllMembersProcessed] = useState(false);
  const [ignorePenalties, setIgnorePenalties] = useState(false);

  const steps = [
    'Review Penalties',
    'Process Members',
    'Confirm & Close'
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(parseFloat(amount) || 0);
  };

  // Load penalty preview and saved penalties when dialog opens
  useEffect(() => {
    if (open && currentPeriod && currentStep === 0) {
      loadPenaltyData();
    }
  }, [open, currentPeriod, currentStep]);

  const loadPenaltyData = async () => {
    try {
      setLoadingPenalties(true);
      
      // Load penalty preview (members who need penalties)
      const preview = await penaltyApi.previewPenalties(
        groupId,
        currentPeriod.period_start,
        currentPeriod.period_end,
        isSchoolFees
      );
      setPenaltyPreview(preview);

      // Load saved penalties (members who already have saved penalties)
      const saved = await penaltyApi.getSavedPenalties(
        groupId,
        currentPeriod.period_end,
        isSchoolFees
      );
      
      // Convert array to object keyed by member_id for easy look,up
      const savedByMember = {};
      saved.saved_penalties.forEach(penalty => {
        savedByMember[penalty.member_id] = penalty;
      });
      setSavedPenalties(savedByMember);
      
      // Initialize memberActions with saved penalties
      const initialActions = {};
      saved.saved_penalties.forEach(penalty => {
        initialActions[penalty.member_id] = {
          action: penalty.status === 'waived' ? 'waive' : 'apply',
          penalty_id: penalty.penalty_id,
          penalty_amount: penalty.penalty_amount,
          reason: penalty.reason,
          is_custom: penalty.is_custom,
          saved: true
        };
      });
      setMemberActions(initialActions);
      
    } catch (error) {
      console.error('Error loading penalty data:', error);
      onError('Failed to load penalty data: ' + error.message);
    } finally {
      setLoadingPenalties(false);
    }
  };

  const handleMemberAction = (memberId, actionData) => {
    setMemberActions(prev => {
      if (actionData.reverted) {
        // Remove from actions if reverted
        const newActions = { ...prev };
        delete newActions[memberId];
        return newActions;
      }
      
      return {
        ...prev,
        [memberId]: actionData
      };
    });
    
    // Update saved penalties state
    if (actionData.reverted) {
      setSavedPenalties(prev => {
        const newSaved = { ...prev };
        delete newSaved[memberId];
        return newSaved;
      });
    } else if (actionData.saved) {
      setSavedPenalties(prev => ({
        ...prev,
        [memberId]: actionData
      }));
    }
  };

  // Check if all members have been processed
  useEffect(() => {
    if (penaltyPreview) {
      const totalPenaltyMembers = [
        ...(penaltyPreview.loan_penalties || []),
        ...(penaltyPreview.advance_penalties || [])
      ];
      
      const processedCount = Object.keys(memberActions).length;
      setAllMembersProcessed(
        processedCount === totalPenaltyMembers.length || 
        totalPenaltyMembers.length === 0
      );
    }
  }, [memberActions, penaltyPreview]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleIgnoreAllPenalties = () => {
    setIgnorePenalties(true);
    setCurrentStep(2); // Skip to final step
  };

  const handleClosePerformanceForm = async () => {
    try {
      setLoading(true);
      
      // Update performance analysis first
      if (onUpdatePerformanceAnalysis) {
        await onUpdatePerformanceAnalysis(false);
      }
  
      // Close the performance period with school fees flag
      await performanceApi.closePerformancePeriod(
        groupId, 
        {
          period_start: currentPeriod.period_start,
          period_end: currentPeriod.period_end,
          closing_notes: closingNotes
        },
        isSchoolFeesMode  // ADD THIS PARAMETER
      );
      
      // Reset state
      resetDialogState();
      
      // Call success callback
      if (onSuccess) {
        await onSuccess();
      }
      
      // Close dialog
      onClose();
      
    } catch (err) {
      console.error('Error closing performance form:', err);
      const errorMessage = err?.response?.data?.error || 'Failed to close performance form';
      
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetDialogState = () => {
    setClosingNotes('');
    setCurrentStep(0);
    setPenaltyPreview(null);
    setSavedPenalties({});
    setMemberActions({});
    setAllMembersProcessed(false);
    setIgnorePenalties(false);
  };

  const handleClose = () => {
    if (!loading) {
      resetDialogState();
      onClose();
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              Penalty System Review
            </Typography>
            
            {loadingPenalties ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading penalty data...</Typography>
              </Box>
            ) : penaltyPreview ? (
              <Box>
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Penalty Summary
                    </Typography>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <Chip 
                        label={`${penaltyPreview.summary?.loan_penalties_count || 0} Loan Penalties`}
                        color="error"
                        icon={<Warning />}
                      />
                      <Chip 
                        label={`${penaltyPreview.summary?.advance_penalties_count || 0} Advance Penalties`}
                        color="warning"
                        icon={<Warning />}
                      />
                      <Chip 
                        label={`${Object.keys(savedPenalties).length} Already Saved`}
                        color="info"
                        icon={<CheckCircle />}
                      />
                      <Chip 
                        label={`Total: ${formatCurrency(penaltyPreview.summary?.total_penalty_amount || 0)}`}
                        color="info"
                      />
                    </Box>
                  </CardContent>
                </Card>

                {(penaltyPreview.loan_penalties?.length > 0 || 
                  penaltyPreview.advance_penalties?.length > 0 ||
                  Object.keys(savedPenalties).length > 0) ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Members with Payment Defaults
                    </Typography>
                    <Typography variant="body2">
                      {penaltyPreview.loan_penalties?.length + penaltyPreview.advance_penalties?.length > 0 
                        ? 'The system has identified members who failed to make required payments. '
                        : ''}
                      {Object.keys(savedPenalties).length > 0 
                        ? `${Object.keys(savedPenalties).length} penalty decision(s) have already been saved. `
                        : ''}
                      Please review each member and decide whether to apply, waive, or customize their penalties.
                      <strong> All decisions are saved immediately and will take effect when you close the period.</strong>
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">
                      No Penalties Required
                    </Typography>
                    <Typography variant="body2">
                      All members have met their payment obligations for this period.
                    </Typography>
                  </Alert>
                )}
              </Box>
            ) : (
              <Alert severity="error">
                Failed to load penalty information. Please try again.
              </Alert>
            )}
          </Box>
        );

      case 1:
        const allPenalties = [
          ...(penaltyPreview?.loan_penalties || []),
          ...(penaltyPreview?.advance_penalties || [])
        ];

        return (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" color="primary">
                Process Member Penalties
              </Typography>
              {allPenalties.length > 0 && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleIgnoreAllPenalties}
                  startIcon={<SkipNext />}
                >
                  Skip All Penalties
                </Button>
              )}
            </Box>

            {allPenalties.length === 0 && Object.keys(savedPenalties).length === 0 ? (
              <Alert severity="success">
                No penalties to process. You can proceed to close the performance period.
              </Alert>
            ) : (
              <Box>
                {Object.keys(savedPenalties).length > 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>{Object.keys(savedPenalties).length} penalty decision(s)</strong> have 
                      already been saved from a previous session. You can review and revert them if needed.
                    </Typography>
                  </Alert>
                )}
                
                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Member</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allPenalties.map((member) => (
                        <PenaltyMemberRow
                          key={member.member_id}
                          member={member}
                          savedPenalty={savedPenalties[member.member_id]}
                          onActionComplete={handleMemberAction}
                          formatCurrency={formatCurrency}
                          groupId={groupId}
                          currentPeriod={currentPeriod}
                          isSchoolFees={isSchoolFees}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            <Box mt={2}>
              <Alert 
                severity={allMembersProcessed ? "success" : "info"}
                icon={allMembersProcessed ? <CheckCircle /> : <Info />}
              >
                <Typography variant="body2">
                  {allMembersProcessed 
                    ? `All members have been processed. You can proceed to the next step.`
                    : `${Object.keys(memberActions).length} of ${allPenalties.length} members processed. 
                       Please make a decision for all members before proceeding.`
                  }
                </Typography>
              </Alert>
            </Box>
          </Box>
        );

        case 2:
          const pendingPenalties = Object.values(memberActions).filter(
            a => a.action !== 'waive' && a.saved
          );
          const waivedPenalties = Object.values(memberActions).filter(
            a => a.action === 'waive' && a.saved
          );
          const customPenalties = Object.values(memberActions).filter(
            a => a.is_custom && a.saved
          );
          
          // FIXED: Calculate unprocessed penalties
          const allPenaltyMembers = [
            ...(penaltyPreview?.loan_penalties || []),
            ...(penaltyPreview?.advance_penalties || [])
          ];
          const unprocessedCount = allPenaltyMembers.length - Object.keys(memberActions).length;
        
          return (
            <Box>
              <Typography variant="h6" gutterBottom color="primary">
                Confirm Period Closure
              </Typography>
              
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Final Confirmation
                </Typography>
                <Typography variant="body2">
                  You are about to close the performance period for <strong>{currentPeriod?.period_name}</strong>. 
                  All saved penalty decisions will be automatically applied to member balances. 
                  This action cannot be undone without administrator intervention.
                </Typography>
              </Alert>
        
              {/* FIXED: Show accurate penalty status */}
              {Object.keys(memberActions).length > 0 ? (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Penalty Actions Summary
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                      <Chip 
                        size="small"
                        label={`${pendingPenalties.length} To Be Applied`}
                        color="success"
                      />
                      <Chip 
                        size="small"
                        label={`${waivedPenalties.length} Waived`}
                        color="error"
                      />
                      <Chip 
                        size="small"
                        label={`${customPenalties.length} Customized`}
                        color="primary"
                      />
                      {unprocessedCount > 0 && (
                        <Chip 
                          size="small"
                          label={`${unprocessedCount} Unprocessed (Will be ignored)`}
                          color="warning"
                          icon={<Warning />}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      Total penalty amount to be applied: <strong>
                        {formatCurrency(
                          pendingPenalties.reduce((sum, p) => sum + parseFloat(p.penalty_amount || 0), 0)
                        )}
                      </strong>
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {ignorePenalties && allPenaltyMembers.length > 0 ? (
                    <Typography variant="body2">
                      All {allPenaltyMembers.length} penalty recommendation(s) have been skipped and will not be applied to member balances.
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      No penalties were identified for this period. All members met their payment obligations.
                    </Typography>
                  )}
                </Alert>
              )}
              
              <TextField
                label="Closing Notes (Optional)"
                multiline
                rows={4}
                value={closingNotes}
                onChange={(e) => setClosingNotes(e.target.value)}
                fullWidth
                sx={{ mt: 2 }}
                disabled={loading}
                helperText="Add any notes about this period closure for future reference"
              />
            </Box>
          );

      default:
        return null;
    }
  };

  const canProceedToNext = () => {
    switch (currentStep) {
      case 0:
        return !loadingPenalties && penaltyPreview !== null;
      case 1:
        const allPenalties = [
          ...(penaltyPreview?.loan_penalties || []),
          ...(penaltyPreview?.advance_penalties || [])
        ];
        
        // FIXED: Allow proceeding if:
        // 1. No penalties exist, OR
        // 2. At least ONE member has been processed (not all), OR
        // 3. Skip was clicked
        const hasProcessedAtLeastOne = Object.keys(memberActions).length > 0;
        return allPenalties.length === 0 || hasProcessedAtLeastOne || ignorePenalties;
        
      case 2:
        return true;
      default:
        return false;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Close Performance Period: {currentPeriod?.period_name}
          </Typography>
          {!loading && (
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ minHeight: 500 }}>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={currentStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {renderStepContent()}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={handleBack}
          disabled={currentStep === 0 || loading}
        >
          Back
        </Button>
        
        <Box sx={{ flex: 1 }} />
        
        {currentStep < steps.length - 1 ? (
          <Button 
            variant="contained"
            onClick={handleNext}
            disabled={!canProceedToNext() || loading}
          >
            Next
          </Button>
        ) : (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleClosePerformanceForm}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? 'Closing Period...' : 'Confirm & Close Period'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ClosePerformance;